import React, { useEffect, useState } from 'react';
import {
	Box,
	Heading,
	Text,
	Button,
	Flex,
	Image,
	Grid,
	IconButton,
	Container,
	Divider,
	HStack,
	VStack,
	Card,
	CardBody,
	Textarea,
	Input,
	FormControl,
	FormLabel,
	Badge,
} from '@chakra-ui/react';
import { connect } from 'react-redux';
import { removeItemFromCart, getUserCart, updateCartItemQuantity } from '../../redux/actions/cartActions';
import { createOrder } from '../../redux/actions/orderActions';
import { FaTrash, FaShoppingBag, FaArrowLeft, FaLock, FaPlus, FaMinus } from 'react-icons/fa';
import { Icon } from '@chakra-ui/react';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import { useNavigate, NavLink } from 'react-router-dom';
import logoImage from '../../assets/logo.png';
import Alert from '../../component/alert';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const fmt = (n: number) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const Cart = ({ cart, removeItemFromCart, getUserCart, createOrder, updateCartItemQuantity }: any) => {
	useEffect(() => {
		getUserCart();
	}, []);

	const navigate = useNavigate();
	const [notes, setNotes] = useState('');
	const [poNumber, setPoNumber] = useState('');

	const handlePlaceOrder = () => {
		createOrder(cart[0].cart_id, notes, poNumber);
		navigate('/confirm');
	};

	const calculateTotals = () => {
		if (!cart || cart.length === 0) return { subtotal: 0, total: 0 };
		const subtotal = cart.reduce(
			(sum: number, item: any) => sum + Number(item.price) * item.quantity,
			0
		);
		return { subtotal, total: subtotal };
	};

	const { subtotal, total } = calculateTotals();
	const itemCount = cart?.reduce((sum: number, item: any) => sum + item.quantity, 0) ?? 0;

	return (
		<Flex direction="column" minH="100vh" bg="gray.50">
			<Navbar />
			<Alert />
			<Box flex={1} py={{ base: 4, md: 8 }}>
				<Container maxW="container.xl" px={{ base: 3, md: 6 }}>
					{/* Header */}
					<Flex align="center" justify="space-between" mb={6} gap={2} flexWrap="wrap">
						<Box>
							<Heading size="lg" color="gray.800">
								Shopping Cart
							</Heading>
							{cart && cart.length > 0 && (
								<Text color="gray.500" fontSize="sm" mt={0.5}>
									{itemCount} item{itemCount !== 1 ? 's' : ''} in your cart
								</Text>
							)}
						</Box>
						<Button
							as={NavLink}
							to="/store"
							leftIcon={<Icon as={FaArrowLeft} />}
							variant="ghost"
							size="sm"
							colorScheme="blue"
						>
							Continue Shopping
						</Button>
					</Flex>

					{!cart || cart.length === 0 ? (
						/* Empty Cart */
						<Card borderRadius="2xl" shadow="sm">
							<CardBody>
								<Flex direction="column" align="center" justify="center" py={16} gap={4}>
									<Flex
										w="80px"
										h="80px"
										borderRadius="full"
										bg="gray.100"
										align="center"
										justify="center"
									>
										<Icon as={FaShoppingBag} boxSize={8} color="gray.400" />
									</Flex>
									<Box textAlign="center">
										<Text fontSize="xl" fontWeight="bold" color="gray.700" mb={1}>
											Your cart is empty
										</Text>
										<Text fontSize="sm" color="gray.400">
											Looks like you haven't added anything yet.
										</Text>
									</Box>
									<Button
										as={NavLink}
										to="/store"
										colorScheme="blue"
										size="lg"
										borderRadius="xl"
										px={8}
										mt={2}
									>
										Start Shopping
									</Button>
								</Flex>
							</CardBody>
						</Card>
					) : (
						<Grid templateColumns={{ base: '1fr', lg: '1fr 380px' }} gap={6} alignItems="start">
							{/* Cart Items */}
							<VStack spacing={3} align="stretch">
								{cart.map((item: any) => (
									<Card key={`${item.product_id}-${item.variant_id || 'base'}`} borderRadius="2xl" shadow="sm" bg="white">
										<CardBody>
											<Flex gap={4} align="center">
												{/* Image */}
												<Box
													flexShrink={0}
													w={{ base: "64px", md: "90px" }}
													h={{ base: "64px", md: "90px" }}
													bg="gray.50"
													borderRadius="xl"
													display="flex"
													alignItems="center"
													justifyContent="center"
													overflow="hidden"
												>
													<Image
														src={item.image ? `${BASE_URL}/api/image?filename=${item.image}` : logoImage}
														alt={item.name}
														maxH="80px"
														maxW="80px"
														objectFit="contain"
													/>
												</Box>

												{/* Info */}
												<Box flex={1} minW={0}>
													<Text fontWeight="semibold" fontSize="md" color="gray.800" noOfLines={2}>
														{item.name}
													</Text>
													{item.variant_label && (
														<Badge colorScheme="purple" fontSize="xs" mt={0.5}>{item.variant_label}</Badge>
													)}
													<Text fontSize="sm" color="gray.500" mt={0.5}>
														{fmt(Number(item.price))} each
													</Text>
													{/* Quantity stepper */}
													<HStack spacing={0} mt={2} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" display="inline-flex">
														<IconButton
															aria-label="Decrease quantity"
															icon={<Icon as={FaMinus} boxSize={2.5} />}
															size="sm"
															variant="ghost"
															h={8}
															w={8}
															isDisabled={item.quantity <= 1}
															onClick={() => updateCartItemQuantity(item.product_id, item.quantity - 1, item.variant_id)}
														/>
														<Text px={3} fontSize="sm" fontWeight="semibold" minW={6} textAlign="center">
															{item.quantity}
														</Text>
														<IconButton
															aria-label="Increase quantity"
															icon={<Icon as={FaPlus} boxSize={2.5} />}
															size="sm"
															variant="ghost"
															h={8}
															w={8}
															isDisabled={false}
															onClick={() => updateCartItemQuantity(item.product_id, item.quantity + 1, item.variant_id)}
														/>
													</HStack>
												</Box>

												{/* Line total + remove */}
												<Flex direction="column" align="flex-end" gap={2} flexShrink={0}>
													<Text fontWeight="bold" fontSize="md" color="gray.800">
														{fmt(Number(item.price) * item.quantity)}
													</Text>
													<IconButton
														aria-label="Remove item"
														icon={<FaTrash />}
														size="sm"
														variant="ghost"
														colorScheme="red"
														borderRadius="lg"
														onClick={() => removeItemFromCart(item.product_id, item.variant_id)}
													/>
												</Flex>
											</Flex>
										</CardBody>
									</Card>
								))}
							</VStack>

							{/* Order Summary — sticky */}
							<Box position={{ lg: 'sticky' }} top={{ lg: '100px' }}>
								<Card borderRadius="2xl" shadow="sm" bg="white">
									<CardBody>
										<Heading size="sm" color="gray.700" mb={4}>
											Order Summary
										</Heading>

										<VStack spacing={3} align="stretch" mb={4}>
											{cart.map((item: any) => (
												<Flex key={`summary-${item.product_id}-${item.variant_id || 'base'}`} justify="space-between" align="flex-start">
													<Box flex={1} mr={2} minW={0}>
														<Text fontSize="sm" color="gray.600" noOfLines={1}>
															{item.name}
															{item.quantity > 1 && (
																<Text as="span" color="gray.400"> ×{item.quantity}</Text>
															)}
														</Text>
														{item.variant_label && (
															<Badge colorScheme="purple" fontSize="2xs">{item.variant_label}</Badge>
														)}
													</Box>
													<Text fontSize="sm" fontWeight="medium" flexShrink={0}>
														{fmt(Number(item.price) * item.quantity)}
													</Text>
												</Flex>
											))}
										</VStack>

										<Divider mb={3} />

										<VStack spacing={2} align="stretch" mb={4}>
											<Flex justify="space-between">
												<Text fontSize="sm" color="gray.500">Subtotal</Text>
												<Text fontSize="sm">{fmt(subtotal)}</Text>
											</Flex>
										</VStack>

										<Divider mb={3} />

										<Flex justify="space-between" align="center" mb={5}>
											<Text fontWeight="bold" fontSize="md" color="gray.800">Total</Text>
											<Text fontWeight="bold" fontSize="xl" color="blue.600">{fmt(total)}</Text>
										</Flex>

										<Divider mb={4} />

										<VStack spacing={3} align="stretch" mb={4}>
											<FormControl>
												<FormLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
													PO Number <Text as="span" color="gray.400" fontWeight="normal">(optional)</Text>
												</FormLabel>
												<Input
													size="sm"
													borderRadius="lg"
													placeholder="e.g. PO-12345"
													value={poNumber}
													onChange={(e) => setPoNumber(e.target.value)}
													bg="gray.50"
												/>
											</FormControl>
											<FormControl>
												<FormLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
													Order Notes <Text as="span" color="gray.400" fontWeight="normal">(optional)</Text>
												</FormLabel>
												<Textarea
													size="sm"
													borderRadius="lg"
													placeholder="Delivery instructions, special requests..."
													value={notes}
													onChange={(e) => setNotes(e.target.value)}
													rows={3}
													bg="gray.50"
													resize="none"
												/>
											</FormControl>
										</VStack>

										<Button
											w="full"
											size="lg"
											colorScheme="green"
											borderRadius="xl"
											leftIcon={<Icon as={FaLock} boxSize={3.5} />}
											onClick={handlePlaceOrder}
											fontWeight="bold"
											fontSize="md"
										>
											Place Order — {fmt(total)}
										</Button>

										<Flex align="center" justify="center" gap={1} mt={3}>
											<Icon as={FaLock} boxSize={3} color="gray.400" />
											<Text fontSize="xs" color="gray.400">Secure checkout</Text>
										</Flex>
									</CardBody>
								</Card>
							</Box>
						</Grid>
					)}
				</Container>
			</Box>
			<Footer />
		</Flex>
	);
};

const mapStateToProps = (state: any) => ({
	cart: state.cart.items,
	order: state.order,
});

const mapDispatchToProps = {
	removeItemFromCart,
	getUserCart,
	createOrder,
	updateCartItemQuantity,
};

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
